import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { ArrowLeft, BookOpen, Printer, Heart } from 'lucide-react';
import { toast } from 'sonner';

export default function NVCKeyIdeas() {
  const navigate = useNavigate();
  const printPage = () => { window.print(); toast.success('Print dialog opened'); };

  const keyIdeas = [
    {
      title: 'The Purpose',
      content: 'The purpose of Nonviolent Communication is to Make Life More Wonderful. Our Hearts are asking to be met!!!',
      color: 'from-rose-900/30 to-pink-900/30'
    },
    {
      title: 'The Goal',
      content: 'The goal of NVC is not to get your way, but rather to create a quality of connection where everyone\'s needs are met through compassionate giving.',
      color: 'from-purple-900/30 to-pink-900/30'
    },
    {
      title: 'The Flow',
      content: 'Giving is joyful when it comes from choice and connection. This flow of connection is characterized by sharing honestly and listening empathetically.',
      color: 'from-blue-900/30 to-cyan-900/30'
    },
    {
      title: 'The Definition of Violence',
      content: 'Violence is understood as any attempt to make someone do something. It is within the consciousness of NVC to use "the protective use of force."',
      color: 'from-red-900/30 to-orange-900/30'
    },
    {
      title: 'Four Aspects of Relating',
      content: 'NVC examines four aspects: 1) consciousness, spirituality, or intention; 2) use of power; 3) use of language; and 4) model of communication.',
      color: 'from-green-900/30 to-emerald-900/30'
    },
    {
      title: 'The Most Precious Gift',
      content: 'The most precious thing one human being can give to another is that which is inside of us: our feelings, needs, and requests.',
      color: 'from-amber-900/30 to-orange-900/30'
    },
    {
      title: 'Honesty, Empathy, Self-Empathy',
      content: 'Honesty is when we share our feelings/needs with another. Empathy is when we are curious and attentive to another\'s sharing. Self-empathy is when we are curious and attentive to our own feelings and needs.',
      color: 'from-cyan-900/30 to-blue-900/30'
    },
    {
      title: 'Our Conditioning',
      content: 'We have been deeply conditioned by domination structures to be disconnected from ourselves and each other. Most people have little awareness of their feelings and needs due to obedience-based conditioning.',
      color: 'from-slate-800/40 to-slate-700/40'
    },
    {
      title: 'Our Greatest Need',
      content: 'One of the greatest needs we have is to contribute to life, our own or someone else\'s. The punishment and reward system distorts this natural desire.',
      color: 'from-purple-900/30 to-violet-900/30'
    },
    {
      title: 'The Four Components',
      content: 'Observations (recordable facts), Feelings (met/unmet need indicators), Needs (universal human longings), Requests (specific actions to meet needs).',
      color: 'from-blue-900/30 to-purple-900/30'
    },
    {
      title: 'Everything is Please or Thank You',
      content: 'Everything we ever say or do is an attempt to meet our own needs. The only thing human beings say is "please" (help me, I have a need) and "thank you" (for helping meet my need).',
      color: 'from-green-900/30 to-teal-900/30'
    },
    {
      title: 'Moralistic Judgments',
      content: 'Moralistic judgments (good/bad, right/wrong) are tragic expressions of unmet needs. Judgments as to whether an action has met a need are very useful and important.',
      color: 'from-red-900/30 to-pink-900/30'
    }
  ];

  // Additional key ideas - Requests and Empathy
  const additionalIdeas = [
    {
      title: 'Requests & Strategies',
      content: 'Requests or strategies are how we communicate to another person our specific ideas of how we could make life more wonderful. A request can be for empathy, honesty or a specific action. These are called either connection requests or action requests.',
      color: 'from-amber-900/30 to-yellow-900/30'
    },
    {
      title: 'Effective Requests',
      content: 'Requests are most effective when made in positive language for a specific, doable present-time action. Requests are most often confused with demands. You will know if you are making a genuine request and not a demand if you are okay with the other person saying no to your request.',
      color: 'from-emerald-900/30 to-green-900/30'
    },
    {
      title: 'The Power of Empathy',
      content: 'Empathy is the ability to share and understand the feelings of another. Empathy heals, honesty brings growth and flows from intention, presence, and focus. Empathy is putting your attention on feelings and needs. Empathy is where you put your attention, not what you say. Empathy can be silent or spoken.',
      color: 'from-rose-900/30 to-pink-900/30'
    },
    {
      title: 'Self-Empathy & Empathy with Others',
      content: 'Self-empathy is putting your attention on your own feelings and needs. Empathy with another person is putting your attention on their feelings and needs. When giving empathy to another person it is not important that you are correct but that you are sincere.',
      color: 'from-blue-900/30 to-indigo-900/30'
    },
    {
      title: 'The Path to Resolution',
      content: 'When two people in conflict can stay so empathically connected to each other\'s feelings and needs that they can repeat them back, then the solution to the situation will naturally arise.',
      color: 'from-violet-900/30 to-purple-900/30'
    },
    {
      title: 'Celebration & Mourning',
      content: 'Gratitude, celebration and mourning are our most powerful sources of energy to live a compassionate life.',
      color: 'from-amber-900/30 to-orange-900/30'
    },
    {
      title: 'You Have Never Done Anything Wrong',
      content: 'Since 1) everything you have ever said or done was an attempt to meet a need, and 2) every decision you made was the best you could think of at that moment, therefore you have never done anything wrong.',
      color: 'from-teal-900/30 to-cyan-900/30'
    }
  ];

  // What NVC is NOT
  const whatNVCisNot = [
    'It is not NVC to try to use NVC to manipulate someone to get what you want.',
    'It is not NVC to try to make another person use NVC.',
    'It is not NVC to tell someone they are not using NVC.',
    'It is not NVC to tell someone they are not using NVC right.',
    'It is not NVC to think you are better than another person because you use NVC.'
  ];

  // Key connection requests
  const connectionRequests = [
    '"Will you please tell me what you heard me say?"',
    '"Will you please tell me what is going on for you now about this situation?"'
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <style>{`@media print { body { background: white !important; } .no-print { display: none !important; } .print-page { background: white !important; color: black !important; } }`}</style>
      <div className="border-b border-slate-700/50 bg-slate-900/90 backdrop-blur-sm sticky top-0 z-50 no-print">
        <div className="container mx-auto px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button onClick={() => navigate('/belonging-ebook')} variant="outline" size="sm" className="border-slate-600 hover:border-slate-500 text-white hover:bg-slate-800"><ArrowLeft className="h-4 w-4 mr-2" />Back</Button>
              <div><h1 className="text-2xl font-bold text-white">Key Ideas and Assumptions of NVC</h1></div>
            </div>
            <div className="flex items-center gap-3">
              <Button onClick={printPage} variant="outline" size="sm" className="border-blue-600 hover:bg-blue-900/20 text-white"><Printer className="h-4 w-4 mr-2" />Print</Button>
              <div className="flex items-center gap-2 text-slate-400 text-sm"><BookOpen className="h-4 w-4" /><span>Page 61</span></div>
            </div>
          </div>
        </div>
      </div>
      <div className="container mx-auto px-6 py-12 print-page">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12"><Heart className="h-20 w-20 text-rose-400 mx-auto mb-6" /><h2 className="text-4xl font-bold text-white mb-4">The Foundation of NVC</h2><p className="text-2xl text-rose-300 italic">Our Hearts are asking to be met!!!</p></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {keyIdeas.map((idea, idx) => (
              <div key={idx} className={`bg-gradient-to-br ${idea.color} border border-white/20 rounded-lg p-6 hover:scale-[1.02] transition-transform`}>
                <h3 className="text-xl font-bold text-white mb-3">{idea.title}</h3>
                <p className="text-slate-100 leading-relaxed">{idea.content}</p>
              </div>
            ))}
          </div>

          {/* Divider */}
          <div className="my-12 flex items-center gap-4">
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-rose-500/50 to-transparent"></div>
            <span className="text-rose-400 text-sm font-medium">DEEPER UNDERSTANDING</span>
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-rose-500/50 to-transparent"></div>
          </div>

          {/* Additional Ideas */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
            {additionalIdeas.map((idea, idx) => (
              <div key={idx} className={`bg-gradient-to-br ${idea.color} border border-white/20 rounded-lg p-6 hover:scale-[1.02] transition-transform`}>
                <h3 className="text-xl font-bold text-white mb-3">{idea.title}</h3>
                <p className="text-slate-100 leading-relaxed">{idea.content}</p>
              </div>
            ))}
          </div>

          {/* Key Connection Requests */}
          <div className="bg-gradient-to-br from-amber-900/40 to-orange-900/40 border-2 border-amber-500/30 rounded-2xl p-8 mb-12">
            <h3 className="text-2xl font-bold text-amber-300 mb-4 text-center">Two Most Important Connection Requests</h3>
            <p className="text-slate-300 mb-6 text-center">In emotionally charged situations, these requests create bridges:</p>
            <div className="space-y-4">
              {connectionRequests.map((req, idx) => (
                <div key={idx} className="bg-slate-900/50 border border-amber-500/20 rounded-lg p-4">
                  <p className="text-xl text-amber-200 text-center italic">{req}</p>
                </div>
              ))}
            </div>
          </div>

          {/* What NVC is NOT */}
          <div className="bg-gradient-to-br from-slate-800/60 to-slate-700/60 border border-slate-600/50 rounded-2xl p-8 mb-12">
            <h3 className="text-2xl font-bold text-white mb-6 text-center">What NVC is NOT</h3>
            <div className="space-y-3">
              {whatNVCisNot.map((item, idx) => (
                <div key={idx} className="flex items-start gap-3">
                  <span className="text-rose-400 text-xl flex-shrink-0">✗</span>
                  <p className="text-slate-200">{item}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Marshall Rosenberg Quote - The Two Questions */}
          <div className="bg-gradient-to-br from-rose-900/30 to-purple-900/30 border-2 border-rose-500/30 rounded-2xl p-10 text-center">
            <div className="mb-6">
              <span className="text-6xl">💝</span>
            </div>
            <h3 className="text-2xl font-bold text-rose-300 mb-6">NVC Answers Two of the Most Asked Questions on the Planet</h3>
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="bg-slate-900/50 rounded-xl p-6 border border-rose-500/20">
                <span className="text-4xl mb-4 block">1️⃣</span>
                <p className="text-xl text-white font-semibold mb-2">"What is alive in you?"</p>
                <p className="text-slate-300 italic">AKA: "How are you doing?"</p>
              </div>
              <div className="bg-slate-900/50 rounded-xl p-6 border border-rose-500/20">
                <span className="text-4xl mb-4 block">2️⃣</span>
                <p className="text-xl text-white font-semibold mb-2">"What ideas do we have to make life more wonderful?"</p>
                <p className="text-slate-300 italic">Finding solutions together</p>
              </div>
            </div>
            <blockquote className="text-xl text-white italic leading-relaxed">
              "These two questions are the heart of NVC — connecting to what's alive in us and finding ways to make life more wonderful for everyone."
            </blockquote>
            <p className="text-rose-300 mt-4">— Marshall Rosenberg</p>
          </div>
        </div>
      </div>
    </div>
  );
}
